import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, ChevronRight, X, AlertTriangle, Store, RefreshCw, Loader2 } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { rupee } from "../../lib/format";
import { EntitySelect } from "./ui";
import { Header } from "./CorporateDashboard";

function scopedRestaurants(ctx, user) {
  let list = ctx.restaurants;
  if (["restaurant_admin", "restaurant_operator"].includes(user.role)) {
    list = list.filter((r) => (user.restaurant_ids || []).includes(r.id));
  } else if (user.role === "restaurant_group_admin") {
    list = list.filter((r) => r.group_id === user.group_id);
  }
  return list;
}

const COLUMNS = [
  { key: "NEW", label: "New", color: "border-blue-500/40" },
  { key: "ACCEPTED", label: "Accepted", color: "border-cyan-500/40" },
  { key: "PREPARING", label: "Preparing", color: "border-amber-500/40" },
  { key: "READY", label: "Ready", color: "border-emerald-500/40" },
];
const NEXT = { NEW: "Accept", ACCEPTED: "Start Preparing", PREPARING: "Mark Ready", READY: "Complete" };

export default function KDS({ ctx, user }) {
  const rests = scopedRestaurants(ctx, user);
  const [rid, setRid] = useState(rests[0]?.id);
  const [data, setData] = useState(null);

  const fetchData = () => rid && api.get(`/kds/${rid}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    if (!rid) return;
    setData(null);
    fetchData();
    const t = setInterval(fetchData, 5000);
    return () => clearInterval(t);
  }, [rid]);

  const advance = async (card) => {
    try {
      await api.post(`/kds/${rid}/suborder/${card.master_id}/${card.sub_id}/advance`);
      toast.success(`${card.order_number} → next stage`);
      fetchData();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const reject = async (card) => {
    try {
      await api.post(`/kds/${rid}/suborder/${card.master_id}/${card.sub_id}/reject`, { reason: "Out of stock" });
      toast.success("Order rejected · refund flagged");
      fetchData();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const selector = (
    <div className="flex items-center gap-3">
      <EntitySelect label="Restaurant" value={rid} onChange={setRid} testid="kds-restaurant-select"
        options={rests.map((r) => ({ value: r.id, label: r.name }))} />
      <button onClick={fetchData} className="card-surface rounded-lg p-2 hover:border-amber-500" data-testid="kds-refresh"><RefreshCw size={15} className="text-amber-400" /></button>
    </div>
  );

  if (!rid) return <Header title="Kitchen Display" sub="No restaurant assigned" />;

  const grouped = COLUMNS.reduce((acc, c) => {
    acc[c.key] = (data?.cards || []).filter((x) => x.status === c.key);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <Header title="Kitchen Display System" sub={`${data?.restaurant?.name || ""} · live · ${data?.cards?.length || 0} active tickets`} right={selector} />
      {!data ? <Loader2 className="animate-spin text-amber-400" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {COLUMNS.map((col) => (
            <div key={col.key} className={`card-surface rounded-2xl p-3 border-t-4 ${col.color}`} data-testid={`kds-col-${col.key}`}>
              <div className="flex items-center justify-between px-1 mb-3">
                <span className="font-display font-bold text-sm uppercase tracking-wide">{col.label}</span>
                <span className="text-xs card-surface-2 rounded-full px-2 py-0.5">{grouped[col.key].length}</span>
              </div>
              <div className="space-y-3 min-h-[100px]">
                {grouped[col.key].map((card) => (
                  <div key={card.sub_id} className={`card-surface-2 rounded-xl p-3 animate-fadeup ${card.delayed ? "ring-1 ring-red-500/60" : ""}`} data-testid={`kds-ticket-${card.sub_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-amber-400">{card.order_number.split("-").pop()}</span>
                      <span className={`text-[11px] flex items-center gap-1 ${card.delayed ? "text-red-400" : "text-slate-500"}`}>
                        {card.delayed && <AlertTriangle size={11} />}<Clock size={11} /> {card.age_min}m
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">Table {card.table_code} · Zone {card.zone}</div>
                    <div className="mt-2 space-y-0.5">
                      {card.items.map((it, i) => (
                        <div key={i} className="text-xs flex justify-between">
                          <span className="font-medium">{it.qty}× {it.name}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => advance(card)} className="gold-btn flex-1 rounded-lg py-1.5 text-xs flex items-center justify-center gap-1" data-testid={`kds-advance-${card.sub_id}`}>
                        {NEXT[card.status]} <ChevronRight size={13} />
                      </button>
                      {card.status === "NEW" && (
                        <button onClick={() => reject(card)} className="card-surface rounded-lg px-2 text-red-400 hover:bg-red-500/10" data-testid={`kds-reject-${card.sub_id}`}><X size={14} /></button>
                      )}
                    </div>
                  </div>
                ))}
                {grouped[col.key].length === 0 && <div className="text-center text-slate-700 text-xs py-6">Empty</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
